/**
 * PasskeyInstructionsModal Component
 *
 * Modal dialog displaying instructions for enrolling physical FIDO2 security keys.
 * Azure AD only supports physical hardware tokens (YubiKey, Google Titan Key, etc.) for work/school accounts.
 * Password manager passkeys (1Password, iCloud Keychain) are NOT supported.
 * Since security keys cannot be enrolled via API (requires WebAuthn ceremony + physical device),
 * this modal guides users to enroll through Azure AD's native flow.
 */

import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  explanationText: {
    color: tokens.colorNeutralForeground2,
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
  },
  stepText: {
    color: tokens.colorNeutralForeground1,
  },
  warningText: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
    marginTop: tokens.spacingVerticalM,
  },
});

interface PasskeyInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PasskeyInstructionsModal: React.FC<
  PasskeyInstructionsModalProps
> = ({ isOpen, onClose }) => {
  const styles = useStyles();

  const handleOpenAzureAD = () => {
    // Open Azure AD security settings in new tab
    window.open('https://mysignins.microsoft.com/security-info', '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Set Up Physical Security Key (FIDO2)</DialogTitle>
          <DialogContent className={styles.content}>
            <Text className={styles.explanationText}>
              Physical FIDO2 security keys must be set up directly through Azure AD.
              This option is for hardware security keys only (YubiKey, Google Titan Key, Feitian, etc.).
            </Text>

            <div>
              <Text weight="semibold">Steps to enroll:</Text>
              <ol className={styles.stepsContainer}>
                <li>
                  <Text className={styles.stepText}>
                    Have your physical FIDO2 security key ready (YubiKey, Google Titan Key, etc.)
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Click &ldquo;Open Azure AD Settings&rdquo; below
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Select &ldquo;Add sign-in method&rdquo;
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Choose &ldquo;Hardware token&rdquo;
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Enter the serial number from your physical security key when prompted
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Follow the on-screen instructions to complete registration
                  </Text>
                </li>
              </ol>
            </div>

            <Text className={styles.warningText}>
              Note: 1Password, iCloud Keychain, and other password manager passkeys are not supported by Azure AD for work/school accounts.
            </Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Close
            </Button>
            <Button appearance="primary" onClick={handleOpenAzureAD}>
              Open Azure AD Settings
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
